// src/pages/profile/ViewPastAssessments.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { getLearnerPastAssestment, downloadLearnerAssessmentPDF } from "../../api/learner/learnerApi";
import useAuth from "../../hooks/useAuth";
import CertificateBg from "../../assets/images/bg.png";
import SBILogo from "../../assets/images/SBI.png";
import logo from "../../assets/images/myassessment.ai_logo.png";
import PerformanceDetails from "./PerformanceDetails";
import AssessmentTable from "./AssessmentTable";

pdfMake.vfs = pdfFonts.vfs;

const ViewPastAssessments = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pastAssestmentData, setPastAssestmentData] = useState([]);
  const [logo64Image, setLogo64Image] = useState(null);
  const [base64Image, setBase64Image] = useState(null);
  const [SBI64Image, setSBI64Image] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPastAssesments = async () => {
      try {
        setLoading(true);
        const response = await getLearnerPastAssestment(user?.email);
        const { data } = response;
        setPastAssestmentData(data?.data || []);
      } catch (error) {
        console.error("Error fetching past assessments:", error);
        setError("Failed to load past assessments");
      } finally {
        setLoading(false);
      }
    };

    if (user?.email) {
      fetchPastAssesments();
    }
  }, [user?.email]);

  useEffect(() => {
    const convertImagesToBase64 = async () => {
      try {
        const bgResponse = await fetch(CertificateBg);
        const bgBlob = await bgResponse.blob();
        const bgReader = new FileReader();
        bgReader.onloadend = () => setBase64Image(bgReader.result);
        bgReader.readAsDataURL(bgBlob);

        const logoResponse = await fetch(logo);
        const logoBlob = await logoResponse.blob();
        const logoReader = new FileReader();
        logoReader.onloadend = () => setLogo64Image(logoReader.result);
        logoReader.readAsDataURL(logoBlob);

        const sbiResponse = await fetch(SBILogo);
        const sbiBlob = await sbiResponse.blob();
        const sbiReader = new FileReader();
        sbiReader.onloadend = () => setSBI64Image(sbiReader.result);
        sbiReader.readAsDataURL(sbiBlob);
      } catch (error) {
        console.error("Error converting images to Base64:", error);
      }
    };

    convertImagesToBase64();
  }, []);

  const handleDownloadPDF = (assessmentId) => {
    downloadLearnerAssessmentPDF(assessmentId);
  };

  const viewAssessmentDetails = (assessmentId) => {
    navigate(`/pastAssessment/${assessmentId}`);
  };

  const generateCertificate = (assessment) => {
    if (!base64Image || (!logo64Image && !SBI64Image)) return;

    const calculateTotalMarks = (marksPerSection) => {
      return Object.values(marksPerSection).reduce((sum, marks) => sum + marks, 0);
    };

    const roundToDecimal = (num, decimals = 1) => {
      if (Number.isInteger(num)) return num;
      return Math.round(num * Math.pow(10, decimals)) / Math.pow(10, decimals);
    };

    const maxScore = calculateTotalMarks(assessment.marks_per_section);
    const userScore = roundToDecimal(assessment.total_score || 0);
    const isSpecialAssessment = assessment.question_code === "C-I6J84E-PO";


    const docDefinition = {
      pageSize: "A4",
      pageOrientation: "landscape",
      pageMargins: [40, 60, 40, 60],
      background: [
        {
          image: base64Image,
          fit: [841, 595],
          absolutePosition: { x: 0, y: 0 },
        },
      ],
      background: [
        {
          image: base64Image,
          fit: [841, 595],
          absolutePosition: { x: 0, y: 0 },
        },
      ],
      content: [
        {
          text: "Certificate of Achievement",
          style: "header",
        },
        {
          text: "This is to certify that",
          style: "subheader",
        },
        {
          text: `${user?.fullName || "Participant"}`,
          style: "awardeeName",
        },
        {
          text: isSpecialAssessment
            ? `has successfully completed the specialized training program conducted by '${assessment.project_name}' with a score of ${userScore} out of ${maxScore}.`
            : `has successfully completed the assessment '${
                assessment.project_name
              }' (${
                assessment.question_code || "N/A"
              }) with a score of ${userScore} out of ${maxScore}.`,
          style: "bodyText",
          margin: [0, 0, 0, 8],
        },
        {
          text: `Date Awarded: ${assessment.created_at.slice(0, 10)}`,
          style: "bodyText",
        },
        {
          text: "Congratulations on your accomplishment!",
          italics: true,
          alignment: "center",
          margin: [0, 30, 0, 0],
        },
        {
          image: isSpecialAssessment ? SBI64Image : logo64Image,
          width: isSpecialAssessment ? 160 : 180,
          height: isSpecialAssessment ? 40 : 80,
          alignment: "center",
          margin: [0, 20, 0, 0],
        },
      ],
      styles: {
        header: {
          fontSize: 28,
          bold: true,
          alignment: "center",
          margin: [0, 30, 0, 10],
          color: "#4a4a4a",
        },
        subheader: {
          fontSize: 16,
          alignment: "center",
          margin: [0, 10, 0, 2],
        },
        awardeeName: {
          fontSize: 22,
          bold: true,
          color: "#d9ab38",
          alignment: "center",
          margin: [0, 4, 0, 15],
        },
        bodyText: {
          fontSize: 14,
          alignment: "center",
          margin: [40, 0, 40, 0],
        },
      },
    };

    pdfMake
      .createPdf(docDefinition)
      .download(`Certificate_${assessment._id}.pdf`);
  };

  if (loading) {
    return <div className="text-center py-4">Loading assessments...</div>;
  }

  if (error) {
    return <div className="text-center py-4 text-red-500">{error}</div>;
  }

  if (pastAssestmentData.length === 0) {
    return (
      <div className="text-center py-4">
        <img src="./no-data.gif" alt="No assessments found" className="mx-auto" />
        <p className="mt-4 text-gray-600">No past assessments found</p>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6 mx-auto">
      <PerformanceDetails pastAssestmentData={pastAssestmentData} />
      <AssessmentTable
        pastAssestmentData={pastAssestmentData}
        viewAssessmentDetails={viewAssessmentDetails}
        handleDownloadPDF={handleDownloadPDF}
        generateCertificate={generateCertificate}
        base64Image={base64Image}
      />
    </div>
  );
};

export default ViewPastAssessments;